import { HtsReadinessPillarKey } from "../constants";
import { HtsReadinessPillarConfig } from "../schemas";

/**
 * Education's dimension keys — named for the same reason as
 * HtsIntegrityDimensionKey: htsReadinessScoring.ts reads `AttainmentLevel`
 * back by key to raise the "no Grade 12 or equivalency" provisional note, and
 * a bare literal there would drift silently against this config.
 */
export const HtsEducationDimensionKey = {
  AttainmentLevel: "attainmentLevel",
  RelevanceToStream: "relevanceToStream",
  ContinuousLearning: "continuousLearning",
} as const;
export type HtsEducationDimensionKey =
  (typeof HtsEducationDimensionKey)[keyof typeof HtsEducationDimensionKey];

export const education: HtsReadinessPillarConfig = {
  key: HtsReadinessPillarKey.EDUCATION,
  label: "Education",
  definition:
    "Current level of education, with \"related\" defined by the target stream (e.g. for policing: criminology, psychology, or law enforcement programs).",
  dataSourceNote: "Derived — entirely from the education entries, including the other-courses/certificates field.",
  dimensions: [
    {
      key: HtsEducationDimensionKey.AttainmentLevel,
      label: "Attainment Level",
      weight: 0.3,
      anchors: {
        0: "No Grade 12 and no equivalency (also an eligibility note).",
        25: "Grade 12 or GED equivalency completed.",
        50: "Diploma program completed, or a completed apprenticeship, Red Seal, or military occupational qualification, or degree program in progress (first/second year).",
        75: "Degree program in progress (third/fourth year), or a related diploma completed, or a completed related apprenticeship or military qualification.",
        100: "Degree completed, or education beyond degree level.",
      },
    },
    {
      key: HtsEducationDimensionKey.RelevanceToStream,
      label: "Relevance to Stream",
      weight: 0.4,
      anchors: {
        0: "No education relevant to the stream.",
        25: "Isolated relevant coursework only.",
        50: "Partially related program (overlapping discipline, minor, or significant electives).",
        75: "Related program in progress.",
        100: "Related program completed.",
      },
    },
    {
      key: HtsEducationDimensionKey.ContinuousLearning,
      label: "Continuous Learning",
      weight: 0.3,
      anchors: {
        0: "No additional courses, certificates, or licences.",
        25: "Certificates held but not current.",
        50: "Current basic certifications (first aid/CPR or equivalent).",
        75: "Multiple current certifications with some role relevance.",
        100: "A deliberate pattern of upskilling: current certifications, role-relevant courses, and role-relevant licences maintained.",
      },
    },
  ],
  topIndicators: ["A related program", "Current first aid", "Certificates nobody told them to get"],
  scoringNotes: [
    "Attainment at 0 carries an eligibility note in the summary but still scores.",
    "Relevance to Stream is scored from the field of the programs actually listed, not from the candidate's own rating of how related their education is: a program in an unrelated field scores 0 even when the candidate calls it \"partially related\".",
    "Score Attainment Level from the stated level of education (including \"degree or diploma: yes\"), even when no program is listed. The one exception: if a listed program was set aside as not credible, score Attainment Level as 25, the Grade 12 floor, which setting an entry aside never goes below.",
    "Foreign credentials score at their assessed Canadian equivalency (WES, ICES, or equivalent). Unassessed foreign credentials score at the self-reported level and are flagged for review; the score is not lowered.",
    "Related programs by stream refers to careers in RCMP, CBSA, Municipal Police, Provincial Police, Indigenous Police Services, Fire Departments, Corrections Services, EMS, Sheriff's Services, and other police and para-military services.",
    "Expiry is neutral where recertification is not required for the target role. All current, role-relevant licences and certifications count equally.",
  ],
};
